import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useConfig } from '../context/ConfigContext';
import { getCourtRate, parseTimeSlotStartHour, formatTimeSlotRange, getCourtLabel, describeEquipmentLine, spinnerRing } from '../utils/bookingUtils';
import { hasReceipt, getBookingReceipt, getOpenPlayReceipt } from '../utils/receiptUtil';
import {
  isPackageBooking,
  getPackageBookingPrice,
  getPackageBookingCourts,
  describePackageBookingTime,
} from '../utils/packageUtils';
import {
  getOpenPlaySessionById,
  getSessionTimeRangeInfo,
  getSessionCourtNumbers,
  getSessionCourtRange,
  formatSessionRangeText,
} from '../utils/openPlayUtil';
import '../css/AdminTimeSlotSelection.css';
import { formatMoney } from '../utils/currencyUtil';
import BookingTotalRow from './BookingTotalRow';
import { getGuestPartialAmount } from '../utils/partialPaymentUtil';

// Formats a plain "yyyy-MM-dd" or ISO date string as "Thursday, August 6,
// 2026" without going through timezone-sensitive Date parsing of a bare
// date string (which JS treats as UTC midnight and can roll back a day).
const formatLongDate = (value) => {
  if (!value) return '';
  const datePart = value.includes('T') ? value.split('T')[0] : value;
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return value;
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
};

// "Reservation details" popup shown when the admin taps a card in the
// Reservation Queue — matches admin-reservation-queue2.png: booking id,
// guest info, schedule breakdown, payment receipt, then Decline / Confirm.
// `booking` is a grouped entry (see bookingGroupUtil.js's
// groupBookingRecords) — `dates` carries every date this reservation
// covers (just one, for an ordinary single-date booking), so the schedule
// section below renders one date-group per entry instead of assuming a
// single booking.date/timeSlots pair.
/**
 * @param {boolean} [locked] - the reservation's dates have all passed and
 *   this admin may not act on it (see canActOnBooking in AdminBooking.jsx).
 *   The card stays fully readable; only Decline/Confirm go away.
 * @param {Function|null} [onPartial] - opens the downpayment dialog. Null
 *   (the default) when the club requires full payment, which is what leaves
 *   this card exactly as it was for every deployment that has not turned
 *   partial payments on.
 * @param {number} [partialPercent] - the configured minimum, for the
 *   button's tooltip. The dialog re-reads and enforces it either way.
 */
function AdminReservationDetailModal({
  booking,
  onClose,
  onConfirm,
  onDecline,
  loading,
  locked = false,
  onPartial = null,
  partialPercent = 0,
}) {
  const { config } = useConfig();
  // Receipt lightbox. Payment screenshots are phone-sized and unreadable
  // at the 260px the card gives them, so tapping one opens it full size.
  const [zoomed, setZoomed] = useState(false);
  // The hosted session an Open Play join belongs to, when it has one.
  //
  // A join records only the single grid cell the guest tapped, so the
  // session is the only place its real hours and courts live. Fetched HERE
  // rather than handed down: this card is the only thing that wants it, it
  // is mounted only while a card is open, so this is one read per card
  // opened either way — and the alternative put data-loading for one line of
  // this modal into a screen that has nothing else to do with it.
  const [openPlaySession, setOpenPlaySession] = useState(null);
  // The receipt image itself, plus how the fetch for it went.
  //
  // Since the receipt split (receiptUtil.js) the record carries only a
  // `hasPaymentScreenshot` flag — the image lives at bookingReceipts/{id} —
  // so "does a receipt exist" and "do we have it in hand" are now two
  // different questions and need two different pieces of state. This modal
  // used to read `booking.paymentScreenshotUrl`, a field the backfill
  // DELETES, so every migrated reservation reported "No receipt uploaded
  // yet." to the admin about to approve its payment.
  const [receiptImage, setReceiptImage] = useState(null);
  // 'idle' (nothing to fetch) | 'loading' | 'loaded' | 'failed'
  const [receiptStatus, setReceiptStatus] = useState('idle');
  // Bumped by Try again; the effect below reads it only to re-run.
  const [receiptAttempt, setReceiptAttempt] = useState(0);

  // Escape closes the lightbox first, leaving the reservation modal open —
  // otherwise the admin would lose their place reviewing the booking.
  // Declared above the `!booking` early return: hooks can't be skipped
  // between renders.
  useEffect(() => {
    if (!zoomed) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') setZoomed(false); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [zoomed]);

  // Also above the early return, for the same reason. `cancelled` guards an
  // admin clicking through several cards quickly: a slow reply for a card
  // already closed must not overwrite a newer one.
  useEffect(() => {
    const sessionId = booking?.isOpenPlayJoin ? booking.sessionId : null;
    if (!sessionId) { setOpenPlaySession(null); return undefined; }
    let cancelled = false;
    getOpenPlaySessionById(sessionId)
      .then((found) => { if (!cancelled) setOpenPlaySession(found); })
      .catch((err) => {
        console.error('Could not load the Open Play session for this join:', err);
        if (!cancelled) setOpenPlaySession(null);
      });
    return () => { cancelled = true; };
  }, [booking]);

  // Fetched as soon as the card opens rather than behind a "view" button:
  // this modal exists to approve or decline a payment, so the admin wants the
  // receipt every time. Same `cancelled` guard as above, for the same reason.
  useEffect(() => {
    if (!booking || !hasReceipt(booking)) {
      setReceiptImage(null);
      setReceiptStatus('idle');
      return undefined;
    }
    let cancelled = false;
    setReceiptStatus('loading');
    // An Open Play join's receipt lives under openPlayReceipts, not
    // bookingReceipts. Both helpers still serve an inline image for a record
    // the backfill has not reached yet.
    const pending = booking.isOpenPlayJoin
      ? getOpenPlayReceipt(booking.id, booking)
      : getBookingReceipt(booking.id, booking);

    pending
      .then((image) => {
        if (cancelled) return;
        setReceiptImage(image);
        // A record that claims a receipt but has no image behind it is a
        // failed read, not an absent receipt — the guest paid and the flag
        // proves it, so this must not fall through to "nothing uploaded".
        setReceiptStatus(image ? 'loaded' : 'failed');
      })
      .catch((err) => {
        console.error('Could not load the payment receipt for this reservation:', err);
        if (cancelled) return;
        setReceiptImage(null);
        setReceiptStatus('failed');
      });

    return () => { cancelled = true; };
  }, [booking, receiptAttempt]);

  if (!booking) return null;

  const dateGroups = Array.isArray(booking.dates) && booking.dates.length > 0
    ? booking.dates
    : [{ date: booking.date, timeSlots: booking.timeSlots }];

  // An event package was sold as one block at one price. Breaking it into
  // court-hours here would price each hour at the ordinary hourly rate — a
  // number the guest was never charged — over a run that also includes the
  // cleanup slot the club is holding but not selling.
  const isPackage = isPackageBooking(booking);
  const packageCourts = isPackage
    ? getPackageBookingCourts(booking).map(court => getCourtLabel(config, court)).join(', ')
    : '';

  // An Open Play join is priced per PLAYER on a session and claims no court
  // at all, so the schedule breakdown below would print hourly rates the
  // guest was never charged against courts they never reserved — the same
  // trap packages fall into, handled the same way.
  const isOpenPlay = !!booking.isOpenPlayJoin;
  // "9:00 PM – 2:00 AM (next day)" for a hosted session, or '' when this join
  // has none (a plain per-cell Open Play slot) or it could not be read.
  const sessionRange = openPlaySession ? getSessionTimeRangeInfo(openPlaySession) : null;
  const sessionRangeText = sessionRange && sessionRange.startHour !== null
    ? formatSessionRangeText(sessionRange.startHour, sessionRange.endHour, sessionRange.endsNextDay)
    : '';
  const sessionVariesByCourt = !!sessionRange?.variesByCourt;
  // Every court the session runs on, each with its own hours. Both sides of
  // midnight are covered: getSessionCourtNumbers unions the session's two
  // halves, so a court used only after 12AM is still listed.
  const sessionCourtRanges = (openPlaySession ? getSessionCourtNumbers(openPlaySession) : [])
    .map((court) => {
      const range = getSessionCourtRange(openPlaySession, court);
      return range
        ? { court, text: formatSessionRangeText(range.startHour, range.endHour, range.endsNextDay) }
        : null;
    })
    .filter(Boolean);
  const openPlayRoster = isOpenPlay
    ? (booking.players?.length
      ? booking.players
      // A join saved before rosters were stored has only the booker.
      : [{ name: booking.fullName || booking.userName, phone: booking.phone }])
    : [];

  const scheduleGroups = (isPackage || isOpenPlay) ? [] : dateGroups.map(group => {
    const courtGroups = {};
    Object.entries(group.timeSlots || {}).forEach(([timeSlot, courts]) => {
      const courtList = Array.isArray(courts) ? courts : Object.keys(courts).map(Number);
      courtList.forEach(court => {
        if (!courtGroups[court]) courtGroups[court] = [];
        // group.date scopes the lookup to the right weekday — rate bands can
        // be set per day (see getCourtRate in bookingUtils.js).
        courtGroups[court].push({ timeSlot, price: getCourtRate(config, court, timeSlot, null, group.date) });
      });
    });
    const sortedCourts = Object.keys(courtGroups).map(Number).sort((a, b) => a - b);
    sortedCourts.forEach(court => {
      courtGroups[court].sort((a, b) => parseTimeSlotStartHour(a.timeSlot) - parseTimeSlotStartHour(b.timeSlot));
    });
    return { date: group.date, sortedCourts, courtGroups };
  });

  const bookedOnLabel = booking.createdAt ? formatLongDate(booking.createdAt) : null;

  /**
   * The body of the Payment Receipt section.
   *
   * Four outcomes, deliberately distinct: nothing was ever uploaded, one is
   * on its way, one exists but could not be read, and one ready to view. The
   * third is the reason this exists — it used to be indistinguishable from
   * the first, telling the admin that a guest who had paid sent nothing.
   *
   * @returns {JSX.Element}
   */
  const renderReceipt = () => {
    if (!hasReceipt(booking)) {
      return <p className="arq-no-receipt">No receipt uploaded yet.</p>;
    }

    if (receiptStatus === 'loading') return spinnerRing('Loading receipt');

    if (receiptStatus === 'failed' || !receiptImage) {
      return (
        <div className="arq-receipt-failed">
          <p className="arq-no-receipt">
            This reservation has a receipt, but it could not be loaded.
          </p>
          <button
            type="button"
            className="arq-receipt-retry"
            onClick={() => setReceiptAttempt((n) => n + 1)}
          >
            Try again
          </button>
        </div>
      );
    }

    return (
      <button
        type="button"
        className="arq-receipt-btn"
        onClick={() => setZoomed(true)}
        title="Click to view full size"
      >
        <img className="arq-receipt" src={receiptImage} alt="Payment receipt" />
        <span className="arq-receipt-hint">Click to enlarge</span>
      </button>
    );
  };

  return (
    <div className="arq-overlay" onClick={onClose}>
      <div className="arq-card" onClick={(e) => e.stopPropagation()}>
        <div className="arq-head">
          <h2>Reservation details</h2>
          <button type="button" className="arq-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <div className="arq-id">{booking.confirmationId}</div>
        <div className="arq-guest">
          <div className="arq-guest-name">{booking.userName}</div>
          {booking.phone && <div>{booking.phone}</div>}
          {bookedOnLabel && <div className="arq-booked-on">Booked {bookedOnLabel}</div>}
        </div>

        <div className="arq-section-label">
          {isOpenPlay
            ? 'Open Play'
            : isPackage
              ? 'Event'
              : `Schedule${scheduleGroups.length > 1 ? ` (${scheduleGroups.length} dates)` : ''}`}
        </div>

        {isOpenPlay && (
          <div>
            <div className="arq-schedule-date">{formatLongDate(booking.date)}</div>
            <div className="arq-court-group">
              <div className="arq-court-lbl">{booking.sportName || 'Court'} Open Play</div>
              <div className="arq-row">
                <span>
                  {/* The SESSION's hours, not the join's `timeSlot`.
                      `timeSlot` records the single grid cell the guest
                      happened to tap — for a session running 9PM to 2AM that
                      is one hour out of five, which read as though the guest
                      had bought an hour of a five-hour rotation. The session
                      is fetched when this modal opens (AdminBooking.jsx); if
                      it can't be read, the tapped cell is still a better
                      answer than nothing. */}
                  {sessionRangeText
                    || (booking.timeSlot ? formatTimeSlotRange(booking.timeSlot) : 'Whole session')}
                </span>
                <span>{formatMoney(Number(booking.pricePerPlayer || 0), config)} / player</span>
              </div>
              {/* WHICH COURTS the session runs on — the thing an admin needs
                  to know to set the venue up, and the one fact the join
                  record itself never carried.
                  When every court shares the session's hours they are listed
                  on one line; when they don't, each gets its own line with
                  its own range, since a single "varies by court" note tells
                  the admin there is a difference without saying what it is. */}
              {sessionCourtRanges.length > 0 && (
                sessionVariesByCourt
                  ? sessionCourtRanges.map(({ court, text }) => (
                    <div className="arq-row arq-row-sub" key={court}>
                      <span>{getCourtLabel(config, court)}: {text}</span>
                    </div>
                  ))
                  : (
                    <div className="arq-row arq-row-sub">
                      <span>{sessionCourtRanges.map(({ court }) => getCourtLabel(config, court)).join(', ')}</span>
                    </div>
                  )
              )}
            </div>

            <div className="arq-section-label">Players ({openPlayRoster.length})</div>
            {openPlayRoster.map((player, index) => (
              // Names repeat within a group, so the key is the position —
              // this list is rendered once and never reordered.
              <div className="arq-row" key={index}>
                <span>{index + 1}. {player.name}</span>
                <span>{player.phone || ''}</span>
              </div>
            ))}
          </div>
        )}

        {isPackage && (
          <div>
            <div className="arq-schedule-date">{formatLongDate(booking.date)}</div>
            <div className="arq-court-group">
              <div className="arq-court-lbl">{booking.packageName || 'Package'}</div>
              <div className="arq-row">
                <span>{describePackageBookingTime(booking)}</span>
                <span>{formatMoney(getPackageBookingPrice(booking), config)}</span>
              </div>
              {packageCourts && (
                <div className="arq-row">
                  <span>{packageCourts}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {scheduleGroups.map((group, idx) => (
          <div key={group.date || idx}>
            <div className="arq-schedule-date">{formatLongDate(group.date)}</div>
            {group.sortedCourts.map(court => (
              <div key={court} className="arq-court-group">
                <div className="arq-court-lbl">{getCourtLabel(config, court)}</div>
                {group.courtGroups[court].map(row => (
                  <div className="arq-row" key={row.timeSlot}>
                    <span>{formatTimeSlotRange(row.timeSlot)}</span>
                    <span>{formatMoney(row.price, config)}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}

        {booking.equipmentRental?.items?.length > 0 && (
          <>
            <div className="arq-section-label">Equipment Rental</div>
            {booking.equipmentRental.items.map(item => (
              <div className="arq-row" key={item.id}>
                <span>{describeEquipmentLine(item)}</span>
                <span>{formatMoney(Number(item.subtotal || 0), config)}</span>
              </div>
            ))}
          </>
        )}

        {booking.bookingFee > 0 && (
          <div className="arq-row">
            <span>Processing Fee</span>
            <span>{formatMoney(Number(booking.bookingFee), config)}</span>
          </div>
        )}

        {/* Same Total line the guest saw. When they ticked "Partial payment"
            at checkout it shows ticked and read-only, with the full total
            struck through and their share — at the percentage saved with
            their choice — as the figure. */}
        <BookingTotalRow
          idPrefix="arq"
          className="arq-total-row"
          total={Number(booking.paymentAmount || 0)}
          config={config}
          // Hidden when "Partial Payment Allowed (%)" is 0 or empty, like
          // everywhere else (see getGuestPartialAmount).
          showPartialOption={getGuestPartialAmount(booking, config) !== null}
          partialChecked
          partialLocked
          partialPercent={Number(booking.guestPartialPercent) || 0}
        />

        <div className="arq-section-label">Payment Receipt</div>
        {renderReceipt()}

        {locked ? (
          <p className="arq-locked-note">
            This reservation has finished — its dates have passed, so it can no longer be
            confirmed or declined here.
          </p>
        ) : (
          <div className="arq-actions">
            <button type="button" className="arq-decline" onClick={onDecline} disabled={loading}>{loading ? '…' : 'Decline'}</button>
            <button type="button" className="arq-confirm" onClick={onConfirm} disabled={loading}>{loading ? '…' : 'Confirm'}</button>
            {/* An Open Play join is excluded — it is priced per player on a
                session and has no reservation total to take a share of. */}
            {onPartial && !isOpenPlay && (
              <button
                type="button"
                className="arq-partial"
                onClick={onPartial}
                disabled={loading}
                title={`Confirm against a downpayment of at least ${partialPercent}% of the total`}
              >
                Confirm (Partial)
              </button>
            )}
          </div>
        )}
      </div>

      {/* Portaled to document.body so it isn't clipped by .arq-card's own
          bounds or capped by this modal's z-index/scroll context. */}
      {zoomed && receiptImage && createPortal(
        // Every click in here calls stopPropagation. A portal renders into
        // document.body but its events still bubble through the REACT tree
        // — i.e. straight into .arq-overlay's onClick={onClose} above — so
        // without this, closing the lightbox also closed the reservation
        // modal behind it.
        <div
          className="arq-zoom-overlay"
          role="dialog"
          aria-label="Payment receipt"
          onClick={(e) => { e.stopPropagation(); setZoomed(false); }}
        >
          <button
            type="button"
            className="arq-zoom-close"
            onClick={(e) => { e.stopPropagation(); setZoomed(false); }}
            aria-label="Close"
          >&times;</button>
          {/* Clicking the image itself shouldn't dismiss it — only the
              backdrop and the × do. */}
          <img
            className="arq-zoom-img"
            src={receiptImage}
            alt="Payment receipt, full size"
            onClick={(e) => e.stopPropagation()}
          />
        </div>,
        document.body
      )}
    </div>
  );
}

export default AdminReservationDetailModal;
